import React from 'react';
import { createPortal } from 'react-dom';

export function Card({ children, className = '', ...props }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-md ${className}`} {...props}>
      {children}
    </div>
  );
}

export function Button({ children, variant = 'primary', size = 'md', className = '', ...props }) {
  const variants = {
    primary: 'bg-[#1E3A8A] text-white hover:bg-[#172d6e] border-transparent',
    accent: 'bg-[#B45309] text-white hover:bg-[#8f420a] border-transparent',
    outline: 'bg-white text-slate-700 hover:bg-slate-100 border-slate-300',
    ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 border-transparent',
    danger: 'bg-rose-600 text-white hover:bg-rose-700 border-transparent',
    success: 'bg-emerald-600 text-white hover:bg-emerald-700 border-transparent',
  };
  const sizes = {
    sm: 'text-xs px-2.5 py-1.5 gap-1',
    md: 'text-sm px-3.5 py-2 gap-1.5',
    lg: 'text-sm px-5 py-2.5 gap-2',
  };
  return (
    <button
      className={`inline-flex items-center justify-center font-medium border rounded-md transition-colors duration-200 active:scale-95 disabled:opacity-50 disabled:pointer-events-none focus-visible:ring-2 focus-visible:ring-[#1E3A8A] focus-visible:ring-offset-1 outline-none ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function Input({ label, className = '', hint, ...props }) {
  return (
    <label className="block">
      {label && <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>}
      <input
        className={`w-full text-sm px-3 py-2 bg-white border border-slate-300 rounded-md placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-[#1E3A8A] focus-visible:border-[#1E3A8A] outline-none transition-colors ${className}`}
        {...props}
      />
      {hint && <span className="block text-xs text-slate-400 mt-1">{hint}</span>}
    </label>
  );
}

export function Select({ label, children, className = '', ...props }) {
  return (
    <label className="block">
      {label && <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>}
      <select
        className={`w-full text-sm px-3 py-2 bg-white border border-slate-300 rounded-md focus-visible:ring-2 focus-visible:ring-[#1E3A8A] outline-none transition-colors ${className}`}
        {...props}
      >
        {children}
      </select>
    </label>
  );
}

export function Textarea({ label, className = '', ...props }) {
  return (
    <label className="block">
      {label && <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>}
      <textarea
        className={`w-full text-sm px-3 py-2 bg-white border border-slate-300 rounded-md placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-[#1E3A8A] outline-none transition-colors ${className}`}
        {...props}
      />
    </label>
  );
}

export function Badge({ children, className = '', ...rest }) {
  return (
    <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded border ${className}`} {...rest}>
      {children}
    </span>
  );
}

export function Modal({ open, onClose, title, children, size = 'md', footer }) {
  if (!open) return null;
  const sizes = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl', xl: 'max-w-6xl' };
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-2 sm:p-8 animate-fade-in" onClick={onClose}>
      <div
        className={`w-full ${sizes[size]} bg-white border border-slate-200 rounded-lg shadow-xl my-2 sm:my-4 animate-fade-up flex flex-col max-h-[calc(100vh-2rem)]`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 shrink-0">
          <h3 className="text-lg font-heading font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors" data-testid="modal-close-btn">✕</button>
        </div>
        <div className="p-4 sm:p-5 overflow-y-auto overflow-x-auto flex-1 min-h-0">{children}</div>
        {footer && <div className="px-5 py-3.5 border-t border-slate-200 flex flex-wrap justify-end gap-2 bg-slate-50 rounded-b-lg shrink-0">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl font-heading font-extrabold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, accent = 'text-[#1E3A8A]', sub, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
          <p className={`text-2xl font-heading font-extrabold mt-1.5 num ${accent}`}>{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
        </div>
        {Icon && (
          <div className="p-2 rounded-md bg-slate-50 border border-slate-100">
            <Icon size={18} className={accent} strokeWidth={2} />
          </div>
        )}
      </div>
    </Card>
  );
}

export function EmptyState({ title, description, icon: Icon, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {Icon && <Icon size={40} className="text-slate-300 mb-3" strokeWidth={1.5} />}
      <p className="text-slate-700 font-medium">{title}</p>
      {description && <p className="text-sm text-slate-400 mt-1 max-w-sm">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Th({ children, className = '', right }) {
  return (
    <th className={`px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide ${right ? 'text-right' : 'text-left'} ${className}`}>
      {children}
    </th>
  );
}

export function Td({ children, className = '', right }) {
  return (
    <td className={`px-3 py-2.5 text-sm text-slate-700 ${right ? 'text-right num' : ''} ${className}`}>
      {children}
    </td>
  );
}
